import React, { useCallback, useEffect, useState } from "react";
import { maintenanceApi, ProcArtifact, ProcOrderDetail } from "../../../api/maintenance";
import { canEditMaintenance } from "../shared/MaintenanceGuard";
import { ORDER_STATUS, conditionMeta, fmtDate, fmtDateTime, errMsg } from "./procUtils";
import ReceiptForm from "./ReceiptForm";
import { X, Download, Mail, RotateCcw, Package, QrCode } from "lucide-react";
import { useNotice } from "../shared/useNotice";

interface Props {
  orderId: number;
  onClose: () => void;
  onChanged: (msg?: string) => void;
}

/**
 * One order, end to end.
 *
 * Note what is NOT here: issuing does not send anything. Delivery is a separate
 * list of attempts, so a failed email is a row with a retry button rather than a
 * blocked order — and downloading the CSV and handing it over in person is a
 * perfectly normal way to finish the job.
 */
const OrderDrawer: React.FC<Props> = ({ orderId, onClose, onChanged }) => {
  const [o, setO] = useState<ProcOrderDetail | null>(null);
  const [error, setError] = useNotice("error", "");
  const [busy, setBusy] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [artifacts, setArtifacts] = useState<ProcArtifact[]>([]);

  const canEdit = canEditMaintenance();

  const load = useCallback(async () => {
    try {
      const r = await maintenanceApi.procGetOrder(orderId);
      setO(r.data);
    } catch (e: any) {
      setError(errMsg(e, "Failed to load order"));
    }
    // Separate call and deliberately swallowed: the order is the screen, and a
    // missing file history must not stop it rendering. It is also empty for
    // every order placed before artifacts existed, which is not an error.
    try {
      const a = await maintenanceApi.procListArtifacts(orderId);
      setArtifacts(a.data.artifacts);
    } catch {
      setArtifacts([]);
    }
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn: () => Promise<any>, msg: string) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      await load();
      onChanged(msg);
    } catch (e: any) {
      setError(errMsg(e, "Action failed"));
    } finally {
      setBusy(false);
    }
  };

  const handleIssue = () => {
    if (!o) return;
    if (
      !window.confirm(
        `Issue ${o.order_no}?\n\nLine items lock and all ${o.total_items} device(s) move to "dispatched". ` +
          `No email is sent — you can email or download the list separately, before or after.`
      )
    )
      return;
    run(() => maintenanceApi.procIssueOrder(orderId), `${o.order_no} issued`);
  };

  const handleEmail = () => {
    if (!o) return;
    run(
      () => maintenanceApi.procDeliverOrder(orderId, { channel: "email" }),
      `Delivery attempted for ${o.order_no}`
    );
  };

  const saveBlob = (data: BlobPart, filename: string, mime: string) => {
    const url = URL.createObjectURL(new Blob([data], { type: mime }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  /** Fetch a stored artifact, generating the pair first if this order has none.
   *
   *  One click rather than a generate step then a download step. Generating is
   *  safe to repeat -- every run writes a new timestamped key rather than
   *  overwriting a file that may already be in a manufacturer's inbox -- but we
   *  only do it when the kind is genuinely absent, so the usual case is a plain
   *  download of the bytes the audit trail already vouches for.
   */
  const downloadArtifact = async (kind: ProcArtifact["kind"], label: string) => {
    if (!o) return;
    setBusy(true);
    setError("");
    try {
      let list = (await maintenanceApi.procListArtifacts(orderId)).data.artifacts;
      if (!list.some((x) => x.kind === kind)) {
        list = (await maintenanceApi.procGenerateArtifacts(orderId)).data.artifacts;
      }
      // newest first from the API; take the newest of this kind
      const art = list.find((x) => x.kind === kind);
      if (!art) throw new Error(`No ${label} was produced for this order`);

      const res = await maintenanceApi.procDownloadArtifact(art.id);
      const suffix = kind === "labels_pdf" ? "labels.pdf" : "devices.csv";
      saveBlob(res.data, `${o.order_no}-${suffix}`, art.content_type);

      await maintenanceApi
        .procDeliverOrder(orderId, { channel: "download", target: `${o.order_no}-${suffix}` })
        .catch(() => undefined);
      onChanged(`${label} downloaded`);
    } catch (e: any) {
      setError(errMsg(e, `Could not download the ${label}`));
    } finally {
      setBusy(false);
      load();
    }
  };

  const handleDownload = async () => {
    if (!o) return;
    const filename = `${o.order_no}-devices.csv`;
    setBusy(true);
    setError("");
    try {
      // Through axios, not window.open. A navigation carries no Authorization
      // header -- the token lives in localStorage and is attached by an
      // interceptor -- and a relative path resolves against the console's own
      // origin instead of the API host, so the old call never reached admin-api.
      const res = await maintenanceApi.procOrderCsv(orderId);
      saveBlob(res.data, filename, "text/csv;charset=utf-8");

      // Recorded only now. It used to be recorded first and unconditionally, so
      // the delivery log read "ok" for downloads that never happened -- the one
      // place that is supposed to answer "what did we actually send them?".
      await maintenanceApi
        .procDeliverOrder(orderId, { channel: "download", target: filename })
        .catch(() => undefined);
      onChanged(`${filename} downloaded`);
    } catch (e: any) {
      setError(errMsg(e, "Could not download the device list"));
    } finally {
      setBusy(false);
      load();
    }
  };

  const handleCancel = () => {
    if (!o) return;
    const reason = window.prompt(
      o.status === "draft"
        ? "Discard this draft? Its devices are released back to the pool.\n\nReason (optional):"
        : "Cancel this order? Devices that have not arrived return to the pool.\n\nReason (optional):"
    );
    if (reason === null) return;
    run(() => maintenanceApi.procCancelOrder(orderId, reason || undefined), `${o.order_no} cancelled`);
  };

  const handleClose = () => {
    if (!o) return;
    const reason = window.prompt(
      `Short-close ${o.order_no}?\n\n${o.outstanding_count} unit(s) are still outstanding. ` +
        `Closing writes them off so the chase list stays a real signal.\n\nReason:`
    );
    if (!reason) return;
    run(() => maintenanceApi.procCloseOrder(orderId, reason), `${o.order_no} short-closed`);
  };

  if (!o) {
    return (
      <div className="proc-drawer-wrap" onClick={onClose}>
        <div className="proc-drawer" onClick={(e) => e.stopPropagation()}>
          <div className="proc-drawer-body">
            {error ? <p className="maint-form-error">{error}</p> : <p className="maint-loading">Loading…</p>}
          </div>
        </div>
      </div>
    );
  }

  const st = ORDER_STATUS[o.effective_status] || { label: o.status, cls: "grey" };
  const isDraft = o.status === "draft";
  const isIssued = o.status === "issued";

  return (
    <div className="proc-drawer-wrap" onClick={onClose}>
      <div className="proc-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="proc-drawer-head">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div className="proc-mini">Order</div>
              <div className="proc-mono" style={{ fontSize: 22, fontWeight: 700 }}>
                {o.order_no}
              </div>
            </div>
            <button className="maint-btn-icon" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
          <div style={{ marginTop: 8 }}>
            <span className={`proc-pill ${st.cls}`}>{st.label}</span>
            <span className="proc-mini" style={{ marginLeft: 8 }}>
              {o.manufacturer_name} · {o.total_items} unit{o.total_items === 1 ? "" : "s"}
              {!isDraft && (
                <>
                  {" · "}
                  <b style={{ color: "#2e7d32" }}>{o.ok_count}</b> ok{" "}
                  <b style={{ color: "#c62828" }}>{o.damaged_count}</b> damaged{" "}
                  <b style={{ color: "#e65100" }}>{o.outstanding_count}</b> outstanding
                </>
              )}
            </span>
          </div>
        </div>

        <div className="proc-drawer-body">
          {error && <p className="maint-form-error">{error}</p>}

          {isDraft && (
            <div className="proc-banner info">
              📝 This is a <b>saved draft</b>. Close it, come back tomorrow, keep adding units —
              nothing has been sent and no device has moved.
            </div>
          )}
          {o.close_reason && (
            <div className="proc-banner good">Short-closed: {o.close_reason}</div>
          )}
          {o.cancel_reason && <div className="proc-banner bad">Cancelled: {o.cancel_reason}</div>}

          {/* artifacts */}
          <div className="proc-group">
            <div className="gh">
              <span className="proc-tag req">Artifacts</span> Download — no email needed
            </div>
            <div className="gb">
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="maint-btn-secondary" disabled={busy}
                        onClick={() => downloadArtifact("labels_pdf", "label sheet")}>
                  <QrCode size={16} /> QR label sheet (PDF)
                </button>
                <button className="maint-btn-secondary" disabled={busy}
                        onClick={() => downloadArtifact("devices_csv", "device list")}>
                  <Download size={16} /> devices.csv
                </button>
              </div>
              <div className="proc-note">
                Every file is kept, so you can re-send exactly what went out before.
              </div>

              {artifacts.length > 0 && (
                <table className="maint-table" style={{ marginTop: 10 }}>
                  <thead>
                    <tr><th>File</th><th>Generated</th><th>By</th><th className="num">Size</th><th>SHA-256</th><th /></tr>
                  </thead>
                  <tbody>
                    {artifacts.map((x) => (
                      <tr key={x.id}>
                        <td>{x.kind === "labels_pdf" ? "Label sheet" : "Device list"}</td>
                        <td>{fmtDateTime(x.generated_at)}</td>
                        <td>{x.generated_by}</td>
                        <td className="num">{Math.max(1, Math.round(x.byte_size / 1024))} KB</td>
                        {/* first 12 of the hash: enough to compare two files by eye,
                            and the full value rides the download as a header */}
                        <td style={{ fontFamily: "monospace", fontSize: 11 }}>{x.sha256.slice(0, 12)}…</td>
                        <td>
                          <button className="maint-btn-small" disabled={busy}
                                  onClick={() => downloadArtifact(x.kind, x.kind === "labels_pdf" ? "label sheet" : "device list")}>
                            Download
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* line items */}
          <div className="proc-group">
            <div className="gh">
              <span className="proc-tag auto">{o.items.length}</span> Line items
            </div>
            <div className="gb">
              <div>
                {o.items.map((i) => {
                  const c = conditionMeta(i.condition);
                  return (
                    <span key={i.procurement_device_id} className="proc-chip">
                      <span className="proc-mono">{i.unique_id}</span>
                      {!isDraft && <span className={`proc-pill ${c.cls}`}>{c.label}</span>}
                      {canEdit && isDraft && (
                        <button
                          type="button"
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                            color: "#c62828",
                            fontWeight: 700,
                            fontFamily: "inherit",
                          }}
                          title="Remove from this draft"
                          disabled={busy}
                          onClick={() =>
                            run(
                              () =>
                                maintenanceApi.procUpdateOrderItems(orderId, {
                                  remove: [i.procurement_device_id],
                                }),
                              `${i.unique_id} removed`
                            )
                          }
                        >
                          ✕
                        </button>
                      )}
                    </span>
                  );
                })}
                {o.items.length === 0 && <span className="proc-mini">No devices yet.</span>}
              </div>
              {isDraft && (
                <div className="proc-note">
                  Add more from the <b>Device pool</b> tab: select generated devices → Add to order.
                </div>
              )}
            </div>
          </div>

          {/* goods receipts */}
          {!isDraft && (
            <div className="proc-group">
              <div className="gh">
                <span className="proc-tag opt">{o.receipts.length}</span> Goods receipts
              </div>
              <div className="gb">
                {o.receipts.length ? (
                  o.receipts.map((r) => (
                    <div key={r.id} style={{ borderBottom: "1px solid #f0f0f0", padding: "9px 0" }}>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <b className="proc-mono" style={{ fontSize: 13 }}>
                          {r.receipt_no}
                        </b>
                        <span className="proc-mini">
                          {fmtDate(r.received_on)} · {r.received_by || "—"}
                        </span>
                      </div>
                      <div className="proc-mini">
                        challan <b>{r.challan_ref || "—"}</b> · {r.ok_count} ok
                        {r.bad_count > 0 && (
                          <span style={{ color: "#c62828" }}> · {r.bad_count} damaged/DOA</span>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <span className="proc-mini">Nothing received yet.</span>
                )}

                {o.outstanding_count > 0 && !o.closed_on && (
                  <div className="proc-banner warn" style={{ marginTop: 12, marginBottom: 0 }}>
                    <b>{o.outstanding_count}</b> unit{o.outstanding_count === 1 ? "" : "s"} still
                    outstanding since {fmtDate(o.issued_on)}. Items with no receipt line stay
                    outstanding — there is no bulk “mark all received”, so a short shipment can't
                    silently vanish.
                  </div>
                )}
                {o.damaged_count > 0 && (
                  <div className="proc-note">
                    Damaged / DOA units are retired with an RMA note and never enter the onboardable
                    pool.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* delivery log */}
          <div className="proc-group">
            <div className="gh">
              <span className="proc-tag opt">Log</span> Delivery attempts
            </div>
            <div className="gb">
              <table className="maint-table" style={{ boxShadow: "none" }}>
                <thead>
                  <tr>
                    <th>Channel</th>
                    <th>To</th>
                    <th>Status</th>
                    <th>When</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {o.deliveries.map((d) => (
                    <tr key={d.id}>
                      <td>
                        {d.channel === "email" ? "✉ Email" : d.channel === "download" ? "⬇ Download" : "🤝 Manual"}
                      </td>
                      <td className="proc-mini">{d.target || "—"}</td>
                      <td>
                        <span className={`proc-pill ${d.status === "failed" ? "red" : "green"}`}>
                          {d.status}
                        </span>
                        {d.error && (
                          <div className="proc-mini" style={{ color: "#c62828" }}>
                            {d.error}
                          </div>
                        )}
                      </td>
                      <td className="proc-mini">
                        {fmtDateTime(d.attempted_at)}
                        <br />
                        {d.attempted_by}
                      </td>
                      <td>
                        {canEdit && d.status === "failed" && d.channel === "email" && (
                          <button className="maint-btn-small" onClick={handleEmail} disabled={busy}>
                            <RotateCcw size={13} /> Retry
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {o.deliveries.length === 0 && (
                    <tr>
                      <td colSpan={5} className="maint-empty">
                        Nothing delivered yet — that's fine, the order stands on its own.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div className="proc-note">
                Delivery is decoupled from the order. A failed email never blocks anything — retry
                it, download the CSV and hand it over, or skip delivery entirely.
              </div>
            </div>
          </div>

          {canEdit && (
            <div className="maint-form-actions" style={{ justifyContent: "flex-start", flexWrap: "wrap" }}>
              {isDraft && (
                <button className="maint-btn-primary" onClick={handleIssue} disabled={busy || !o.items.length}>
                  Issue order →
                </button>
              )}
              {isIssued && !o.closed_on && o.outstanding_count > 0 && (
                <button className="maint-btn-primary" onClick={() => setShowReceipt(true)} disabled={busy}>
                  <Package size={16} /> Record a receipt…
                </button>
              )}
              {!isDraft && o.manufacturer_email && (
                <button className="maint-btn-secondary" onClick={handleEmail} disabled={busy}>
                  <Mail size={16} /> Email to manufacturer
                </button>
              )}
              {isIssued && !o.closed_on && o.outstanding_count > 0 && o.ok_count + o.damaged_count > 0 && (
                <button className="maint-btn-secondary" onClick={handleClose} disabled={busy}>
                  Short-close…
                </button>
              )}
              {o.status !== "cancelled" && (isDraft || o.ok_count + o.damaged_count === 0) && (
                <button className="maint-btn-danger" onClick={handleCancel} disabled={busy}>
                  {isDraft ? "Discard draft" : "Cancel order"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {showReceipt && (
        <ReceiptForm
          order={o}
          onClose={() => setShowReceipt(false)}
          onDone={(res) => {
            setShowReceipt(false);
            load();
            onChanged(
              `${res.receipt_no}: ${res.received_ok} received, ${res.retired_bad} retired, ${res.still_outstanding} still outstanding`
            );
          }}
        />
      )}
    </div>
  );
};

export default OrderDrawer;
